/**
 * Reactive online/offline status.
 *
 * `navigator.onLine` is only a hint — it reports whether the device has a
 * network interface, not whether Convex is reachable. It is accurate enough
 * for the read-only banner, and being wrong in the optimistic direction is
 * harmless: the mutation simply fails and toasts.
 */
import { useEffect, useState } from "react";

export function useIsOffline(): boolean {
  const [isOffline, setIsOffline] = useState(() =>
    typeof navigator !== "undefined" ? !navigator.onLine : false,
  );

  useEffect(() => {
    const goOnline = () => setIsOffline(false);
    const goOffline = () => setIsOffline(true);

    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);

    // Resync on mount — the events can fire before React hydrates.
    setIsOffline(!navigator.onLine);

    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  return isOffline;
}
